import React from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { useIremboSubscriptionPaymentFlow } from "../../hooks/useIremboSubscriptionPaymentFlow";
import type { Subscription } from "../../Graphql/types/subscription";
import { dialogTitleCenterSx } from "../common/dialogTitleCenterSx";

interface ActivateSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  subscription: Subscription;
}

const ActivateSubscriptionModal: React.FC<ActivateSubscriptionModalProps> = ({
  open,
  onClose,
  subscription
}) => {
  const { t } = useKivunovaTranslation();
  const { startCheckout, isRunning } = useIremboSubscriptionPaymentFlow();
  const [checkoutError, setCheckoutError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setCheckoutError(null);
    }
  }, [open]);

  const handleActivateAndPay = async () => {
    setCheckoutError(null);
    await startCheckout(subscription.subscriptionId, {
      onInvoiceReady: onClose,
      onCheckoutError: setCheckoutError,
      redirectOnIncomplete: true
    });
  };

  const planName = subscription.planName || subscription.planId;
  const descriptionTemplate = t(
    "subscription-activate-description",
    "Complete payment to activate your subscription to {{planName}}.",
    { planName: "{{planName}}" }
  );
  const parts = descriptionTemplate.split("{{planName}}");
  const before = parts[0];
  const after = parts.length > 1 ? parts[1] : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ ...dialogTitleCenterSx, pb: 1 }}>
        <Typography variant="h5" fontWeight="bold">
          {t("subscription-activate-title", "Activate subscription")}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 3, sm: 4 }, pb: 2 }}>
        {checkoutError ? (
          <Alert severity="error" sx={{ mb: 2 }} role="alert">
            {checkoutError}
          </Alert>
        ) : null}
        <Box
          sx={{
            borderRadius: 2,
            bgcolor: "rgba(0,0,0,0.02)",
            border: "1px solid rgba(0,0,0,0.08)",
            p: 2.25
          }}
        >
          <Typography
            variant="body1"
            color="text.primary"
            sx={{ mb: 1.25, textAlign: "center" }}
          >
            {before}
            <strong>{planName}</strong>
            {after}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            {t(
              "subscription-activate-payment-options",
              "The Irembo payment window will open so you can complete payment for this subscription."
            )}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 3, sm: 4 },
          pb: 3,
          pt: 1,
          justifyContent: "center",
          gap: 1.25
        }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ minWidth: 120, fontWeight: 600, textTransform: "none" }}
        >
          {t("cancel", "Cancel")}
        </Button>
        <LoadingButton
          variant="contained"
          loading={isRunning}
          onClick={() => void handleActivateAndPay()}
          sx={{
            backgroundColor: BRAND_COLOR,
            color: "white",
            "&:hover": {
              backgroundColor: BRAND_HOVER,
              transform: "translateY(-1px)",
              boxShadow: "0 8px 20px rgba(147, 109, 174, 0.35)"
            },
            fontWeight: "bold",
            minWidth: 220,
            borderRadius: 2,
            textTransform: "none",
            boxShadow: "0 4px 12px rgba(147, 109, 174, 0.3)",
            transition: "all 0.2s ease"
          }}
        >
          {t("subscription-activate-and-pay", "Activate and pay")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ActivateSubscriptionModal;
